import axios from 'axios';

// Configure API URL based on environment
// In development with separate servers, point to the backend directly
// In production, use relative URLs (empty string) as frontend/backend are served together
const isDevelopment = process.env.NODE_ENV === 'development';
const API_URL = isDevelopment ? 'http://localhost:3001' : '';

// Create axios instance with credentials
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important: ensures cookies are sent with requests
});

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log detailed error information for debugging, but don't automatically logout
    if (error.response) {
      // Log errors but don't spam the console for auth errors
      if (error.response.status === 401 || error.response.status === 403) {
        console.warn('Authentication error:', error.response.status);
      } else {
        console.error('API Error:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          url: error.config.url
        });
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

// Auth API
export const getCurrentUser = async (): Promise<User> => {
  const { data } = await api.get('/api/auth/user');
  return data;
};

// Types
export interface User {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  image_url: string | null;
  google_id?: string;
  auto_sync_calendar?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Birthday {
  id: string;
  user_id: string;
  name: string;
  birth_date: string;
  reminder_days?: number[];
  google_calendar_event_id?: string | null;
  emoji?: string;
  created_at: string;
  updated_at: string;
  reminders?: Reminder[];
  gifts?: Gift[];
}

export interface Reminder {
  id: string;
  birthday_id: string;
  days_before: number;
  created_at: string;
  updated_at: string;
}

export interface Gift {
  id: string;
  birthday_id: string;
  name: string;
  price?: number;
  url?: string;
  notes?: string;
  is_purchased: boolean;
  created_at: string;
  updated_at: string;
  images?: GiftImage[];
}

export interface GiftImage {
  id: string;
  gift_id: string;
  image_url: string;
  created_at: string;
  updated_at: string;
}

// API functions for birthdays
export const getBirthdays = async (): Promise<Birthday[]> => {
  const { data } = await api.get('/api/birthdays');
  return data;
};

export const getBirthday = async (id: string): Promise<Birthday> => {
  const { data } = await api.get(`/api/birthdays/${id}`);
  return data;
};

export const createBirthday = async (birthday: Pick<Birthday, 'name' | 'birth_date'> & Partial<Pick<Birthday, 'emoji'>>): Promise<Birthday> => {
  const { data } = await api.post('/api/birthdays', birthday);
  return data;
};

export const updateBirthday = async (id: string, birthday: Partial<Pick<Birthday, 'name' | 'birth_date' | 'emoji' | 'reminder_days' | 'google_calendar_event_id'>>): Promise<Birthday> => {
  const { data } = await api.put(`/api/birthdays/${id}`, birthday);
  return data;
};

export const deleteBirthday = async (id: string): Promise<void> => {
  await api.delete(`/api/birthdays/${id}`);
};

// Reminders API
export const getReminders = async (birthdayId: string): Promise<Reminder[]> => {
  const { data } = await api.get(`/api/reminders/birthday/${birthdayId}`);
  return data;
};

export const createReminder = async (reminder: Pick<Reminder, 'birthday_id' | 'days_before'>): Promise<Reminder> => {
  const { data } = await api.post('/api/reminders', reminder);
  return data;
};

export const updateReminder = async (id: string, days_before: number): Promise<Reminder> => {
  const { data } = await api.put(`/api/reminders/${id}`, { days_before });
  return data;
};

export const deleteReminder = async (id: string): Promise<void> => {
  await api.delete(`/api/reminders/${id}`);
};

// Gifts API
export const getGifts = async (birthdayId: string): Promise<Gift[]> => {
  const { data } = await api.get(`/api/gifts/birthday/${birthdayId}`);
  return data;
};

export const getGift = async (id: string): Promise<Gift> => {
  const { data } = await api.get(`/api/gifts/${id}`);
  return data;
};

export const createGift = async (gift: Pick<Gift, 'birthday_id' | 'name'> & Partial<Pick<Gift, 'price' | 'url' | 'notes' | 'is_purchased'>>): Promise<Gift> => {
  const { data } = await api.post('/api/gifts', gift);
  return data;
};

export const updateGift = async (id: string, gift: Pick<Gift, 'name'> & Partial<Pick<Gift, 'price' | 'url' | 'notes' | 'is_purchased'>>): Promise<Gift> => {
  const { data } = await api.put(`/api/gifts/${id}`, gift);
  return data;
};

export const toggleGiftPurchased = async (id: string): Promise<Gift> => {
  const { data } = await api.patch(`/api/gifts/${id}/toggle-purchased`);
  return data;
};

export const deleteGift = async (id: string): Promise<void> => {
  await api.delete(`/api/gifts/${id}`);
};

// Gift Images API
export const uploadGiftImage = async (giftId: string, file: File): Promise<GiftImage> => {
  const formData = new FormData();
  formData.append('image', file);
  
  const { data } = await api.post(`/api/gifts/${giftId}/images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

export const deleteGiftImage = async (giftId: string, imageId: string): Promise<void> => {
  await api.delete(`/api/gifts/${giftId}/images/${imageId}`);
};

// User API
export const getUserProfile = async (): Promise<User> => {
  const { data } = await api.get('/api/users/profile');
  return data;
};

export const updateUserProfile = async (profile: Partial<Pick<User, 'first_name' | 'last_name' | 'image_url' | 'auto_sync_calendar'>>): Promise<User> => {
  const { data } = await api.put('/api/users/profile', profile);
  return data;
};

export const deleteUserAccount = async (): Promise<void> => {
  await api.delete('/api/users/account');
};

export const getUserStats = async (): Promise<{
  total_birthdays: number;
  upcoming_birthdays: number;
  total_gifts: number;
  unpurchased_gifts: number;
}> => {
  const { data } = await api.get('/api/users/stats');
  return data;
};

export const logout = async (): Promise<void> => {
  try {
    await api.post('/api/auth/logout');
  } catch (error) {
    console.error('Error during logout API call:', error);
    // Continue with local logout even if the API call fails
  } finally {
    // For cookies that might be set with JavaScript
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }
};

// Collection Links API
export const getCollectionLink = async (): Promise<{ link_code: string }> => {
  const { data } = await api.get('/api/users/collection-link');
  return data;
};

export const submitBirthdayViaCollectionLink = async (
  linkCode: string, 
  birthdayInfo: { 
    name: string; 
    birth_date: string; 
    emoji?: string; 
    gifts?: string[] 
  }
): Promise<void> => {
  await api.post(`/api/users/collection/${linkCode}`, birthdayInfo);
};